import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Text,
} from '@chakra-ui/react';
import {
  TableRoot,
  TableHeader,
  TableBody,
  TableRow,
  TableColumnHeader,
  TableCell,
} from '@/components/ui/table';
import {
  MenuRoot,
  MenuTrigger,
  MenuContent,
  MenuItem,
} from '@/components/ui/menu';
import { Tooltip } from '@/components/ui/tooltip';
import { toaster } from '@/components/ui/toaster';
import { useState, useEffect } from 'react';
import { FiPower, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { RiWhatsappLine } from 'react-icons/ri';
import { Channel } from '@/features/channels/channels.types';

interface WhatsAppChannelTableProps {
  channels: Channel[];
  onStatusClick?: (channel: Channel) => void;
  onStartChannel?: (channelId: string) => void;
  onStopChannel?: (channelId: string) => void;
  renderActions?: (channel: Channel) => React.ReactNode;
  transitioningChannels?: string[];
}

export function WhatsAppChannelTable({
  channels,
  onStatusClick,
  onStartChannel,
  onStopChannel,
  renderActions,
  transitioningChannels = [],
}: WhatsAppChannelTableProps) {
  const [localTransitioningIds, setLocalTransitioningIds] = useState<string[]>(
    [],
  );

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  // Filter WhatsApp channels and apply search
  const filteredChannels = channels.filter(
    (channel) => channel.type === 'whatsapp',
  );

  // Pagination
  const total = filteredChannels.length;
  const startIndex = (page - 1) * perPage;
  const endIndex = startIndex + perPage;
  const paginatedChannels = filteredChannels.slice(startIndex, endIndex);

  // Reset page when search changes
  useEffect(() => {
    setPage(1);
  }, []);

  const isChannelTransitioning = (channelId: string) => {
    return (
      localTransitioningIds.includes(channelId) ||
      transitioningChannels.includes(channelId)
    );
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      WORKING: 'green',
      CONNECTED: 'green',
      PENDING: 'yellow',
      FAILED: 'red',
      STARTING: 'blue',
      STOPPED: 'gray',
      SCAN_QR_CODE: 'purple',
      STOPPING: 'orange',
      INITIALIZING: 'blue',
    };
    return statusColors[status?.toUpperCase()] || 'gray';
  };

  const handleStart = (channelId: string) => {
    setLocalTransitioningIds((prev) => [...prev, channelId]);

    if (onStartChannel) {
      Promise.resolve(onStartChannel(channelId)).finally(() => {
        setLocalTransitioningIds((prev) =>
          prev.filter((id) => id !== channelId),
        );
      });
    } else {
      setTimeout(() => {
        setLocalTransitioningIds((prev) =>
          prev.filter((id) => id !== channelId),
        );
        toaster.create({
          title: 'Session Started',
          description: 'WhatsApp session is starting...',
          type: 'success',
        });
      }, 1000);
    }
  };

  const handleStop = (channelId: string) => {
    setLocalTransitioningIds((prev) => [...prev, channelId]);

    if (onStopChannel) {
      Promise.resolve(onStopChannel(channelId)).finally(() => {
        setLocalTransitioningIds((prev) =>
          prev.filter((id) => id !== channelId),
        );
      });
    } else {
      setTimeout(() => {
        setLocalTransitioningIds((prev) =>
          prev.filter((id) => id !== channelId),
        );
        toaster.create({
          title: 'Session Stopped',
          description: 'WhatsApp session has been stopped',
          type: 'success',
        });
      }, 1000);
    }
  };

  const handleStatusClick = (channel: Channel) => {
    if (channel.status?.toUpperCase() === 'SCAN_QR_CODE') {
      onStatusClick?.(channel);
    }
  };

  const renderStatus = (channel: Channel) => {
    const status = channel.status?.toUpperCase() || 'UNKNOWN';
    const isTransitioning = isChannelTransitioning(channel.id);
    const isInTransitionState = [
      'STARTING',
      'STOPPING',
      'PENDING',
      'INITIALIZING',
    ].includes(status);
    const shouldShowSpinner = isTransitioning || isInTransitionState;

    if (shouldShowSpinner) {
      return (
        <Flex align="center">
          <Spinner size="sm" mr={2} />
          <Badge colorScheme={getStatusColor(status)}>
            {isTransitioning ? 'UPDATING' : status}
          </Badge>
        </Flex>
      );
    }

    return (
      <Tooltip content={getStatusDescription(status)}>
        <Badge
          colorScheme={getStatusColor(status)}
          cursor={status === 'SCAN_QR_CODE' ? 'pointer' : 'default'}
          onClick={() => handleStatusClick(channel)}
        >
          {status}
        </Badge>
      </Tooltip>
    );
  };

  const getStatusDescription = (status: string): string => {
    const descriptions: Record<string, string> = {
      WORKING: 'Channel is connected and working',
      CONNECTED: 'Channel is connected and ready',
      PENDING: 'Channel is being initialized',
      FAILED: 'Channel connection failed',
      STARTING: 'Channel is starting up',
      STOPPED: 'Channel is stopped',
      SCAN_QR_CODE: 'Click to scan QR code',
      STOPPING: 'Channel is stopping',
      INITIALIZING: 'Channel is initializing',
    };
    return descriptions[status] || 'Unknown status';
  };

  const renderActionButtons = (channel: Channel) => {
    const status = channel.status?.toUpperCase() || '';
    const isActive = ['WORKING', 'SCAN_QR_CODE', 'CONNECTED'].includes(status);

    return (
      <Tooltip content={isActive ? 'Stop Channel' : 'Start Channel'}>
        <IconButton
          aria-label={isActive ? 'Stop session' : 'Start session'}
          size="sm"
          variant="outline"
          borderRadius="xl"
          loading={isChannelTransitioning(channel.id)}
          onClick={() =>
            isActive ? handleStop(channel.id) : handleStart(channel.id)
          }
          bg={isActive ? 'red.50' : 'green.50'}
          borderColor={isActive ? 'red.200' : 'green.200'}
          color={isActive ? 'red.600' : 'green.600'}
          _hover={{
            bg: isActive ? 'red.100' : 'green.100',
            borderColor: isActive ? 'red.300' : 'green.300',
            color: isActive ? 'red.700' : 'green.700',
          }}
          _active={{
            bg: isActive ? 'red.200' : 'green.200',
            borderColor: isActive ? 'red.400' : 'green.400',
          }}
          transition="all 0.2s"
        >
          <FiPower />
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <Box display="flex" flexDirection="column" height="100%">
      {/* Table Container - Following channelList design */}
      <Box
        position="relative"
        flex="1"
        overflowX="auto"
        overflowY="auto"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="xl"
        bg="white"
        boxShadow="md"
        css={{
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#cbd5e0',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a0aec0',
          },
        }}
      >
        <TableRoot>
          <TableHeader>
            <TableRow>
              <TableColumnHeader
                py={3}
                px={3}
                fontSize="xs"
                fontWeight="semibold"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="wider"
                bg="white"
                borderBottomWidth="1px"
                borderBottomColor="gray.200"
              >
                Channel Name
              </TableColumnHeader>
              <TableColumnHeader
                py={3}
                px={3}
                fontSize="xs"
                fontWeight="semibold"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="wider"
                bg="white"
                borderBottomWidth="1px"
                borderBottomColor="gray.200"
              >
                Phone
              </TableColumnHeader>
              <TableColumnHeader
                py={3}
                px={3}
                fontSize="xs"
                fontWeight="semibold"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="wider"
                bg="white"
                borderBottomWidth="1px"
                borderBottomColor="gray.200"
              >
                Status
              </TableColumnHeader>
              <TableColumnHeader
                py={3}
                px={3}
                fontSize="xs"
                fontWeight="semibold"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="wider"
                bg="white"
                borderBottomWidth="1px"
                borderBottomColor="gray.200"
              >
                Actions
              </TableColumnHeader>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedChannels.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} textAlign="center" py={6}>
                  No WhatsApp channels found
                </TableCell>
              </TableRow>
            ) : (
              paginatedChannels.map((channel) => (
                <TableRow
                  key={channel.id}
                  transition="background-color 0.2s"
                  _hover={{
                    backgroundColor: 'gray.50',
                  }}
                >
                  <TableCell py={3} px={3}>
                    <Flex align="center" gap={3}>
                      <Icon as={RiWhatsappLine} color="green.500" boxSize={5} />
                      <Text fontWeight="semibold" color="gray.900">
                        {channel.name}
                      </Text>
                    </Flex>
                  </TableCell>
                  <TableCell py={3} px={3}>
                    <Text color="gray.700" fontSize="sm">
                      {channel.phone || '-'}
                    </Text>
                  </TableCell>
                  <TableCell py={3} px={3}>
                    {renderStatus(channel)}
                  </TableCell>
                  <TableCell py={3} px={3}>
                    <Flex gap={2}>
                      {renderActionButtons(channel)}
                      {renderActions && renderActions(channel)}
                    </Flex>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </TableRoot>
      </Box>

      {/* Pagination Controls - Following channelList design */}
      <Box
        mt={6}
        py={4}
        px={6}
        bg="gray.50"
        borderRadius="xl"
        borderWidth="1px"
        borderColor="gray.200"
      >
        <Flex justify="space-between" align="center">
          <HStack gap={6}>
            <Text fontSize="sm" color="gray.600" fontWeight="medium">
              Total:{' '}
              <Text as="span" fontWeight="bold" color="gray.900">
                {total}
              </Text>{' '}
              WhatsApp channels
            </Text>

            <HStack gap={3}>
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                Rows per page:
              </Text>
              <MenuRoot>
                <MenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    bg="white"
                    borderColor="gray.300"
                    _hover={{ borderColor: 'gray.400' }}
                  >
                    {perPage}
                    <FiChevronRight />
                  </Button>
                </MenuTrigger>
                <MenuContent>
                  {[10, 25, 50, 100].map((value) => (
                    <MenuItem
                      key={value}
                      value={value.toString()}
                      onClick={() => {
                        setPerPage(value);
                        setPage(1);
                      }}
                    >
                      {value}
                    </MenuItem>
                  ))}
                </MenuContent>
              </MenuRoot>
            </HStack>
          </HStack>

          <HStack gap={4}>
            <Text fontSize="sm" color="gray.600" fontWeight="medium">
              {total > 0
                ? `${startIndex + 1}-${Math.min(endIndex, total)} of ${total}`
                : '0 items'}
            </Text>
            <HStack gap={1}>
              <IconButton
                aria-label="Previous page"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                variant="outline"
                size="sm"
                bg="white"
                borderColor="gray.300"
                _hover={{ borderColor: 'gray.400', bg: 'gray.50' }}
                _disabled={{
                  opacity: 0.4,
                  cursor: 'not-allowed',
                  _hover: { bg: 'white', borderColor: 'gray.300' },
                }}
              >
                <FiChevronLeft />
              </IconButton>
              <IconButton
                aria-label="Next page"
                disabled={endIndex >= total}
                onClick={() => setPage(page + 1)}
                variant="outline"
                size="sm"
                bg="white"
                borderColor="gray.300"
                _hover={{ borderColor: 'gray.400', bg: 'gray.50' }}
                _disabled={{
                  opacity: 0.4,
                  cursor: 'not-allowed',
                  _hover: { bg: 'white', borderColor: 'gray.300' },
                }}
              >
                <FiChevronRight />
              </IconButton>
            </HStack>
          </HStack>
        </Flex>
      </Box>
    </Box>
  );
}
